import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import api from './client';

// Query keys
export const canvasKeys = {
    all: ['canvases'],
    detail: (id) => ['canvases', id],
    nodes: (canvasId) => ['canvases', canvasId, 'nodes'],
    edges: (canvasId) => ['canvases', canvasId, 'edges'],
    groups: (canvasId) => ['canvases', canvasId, 'groups'],
    media: (canvasId) => ['canvases', canvasId, 'generated-media'],
    artboards: (canvasId) => ['canvases', canvasId, 'artboards'],
    uiComponents: (canvasId) => ['canvases', canvasId, 'ui-components'],
    uiComponentsByArtboard: (artboardId) => ['artboards', artboardId, 'ui-components']
};

export const designKitKeys = {
    all: ['design-kit'],
    merged: (canvasId) => ['design-kit', 'merged', canvasId],
    components: (layer, device) => ['design-kit', 'components', layer, device],
    tokens: ['design-kit', 'tokens']
};

// ============================================
// Canvas Queries
// ============================================

/**
 * Get all canvases
 */
export const useCanvases = () => {
    return useQuery({
        queryKey: canvasKeys.all,
        queryFn: async () => {
            const { data } = await api.get('/canvases');
            return data;
        }
    });
};

/**
 * Get canvas by ID
 */
export const useCanvas = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.detail(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/canvases/${canvasId}`);
            return data;
        },
        enabled: !!canvasId
    });
};

// ============================================
// Canvas Mutations
// ============================================

/**
 * Create a new canvas
 */
export const useCreateCanvas = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (canvasData) => {
            const { data } = await api.post('/canvases', canvasData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.all });
        }
    });
};

/**
 * Update a canvas
 */
export const useUpdateCanvas = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ canvasId, data: canvasData }) => {
            const { data } = await api.put(`/canvases/${canvasId}`, canvasData);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.all });
            queryClient.invalidateQueries({ queryKey: canvasKeys.detail(data.id) });
        }
    });
};

/**
 * Delete a canvas
 */
export const useDeleteCanvas = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (canvasId) => {
            await api.delete(`/canvases/${canvasId}`);
            return canvasId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.all });
        }
    });
};

// ============================================
// Nested Node Queries & Mutations
// ============================================

/**
 * Get nodes for a canvas
 */
export const useCanvasNodes = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.nodes(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/canvases/${canvasId}/nodes`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Create a node in a canvas
 */
export const useCreateNode = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (nodeData) => {
            const { data } = await api.post(`/canvases/${canvasId}/nodes`, nodeData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.nodes(canvasId) });
        }
    });
};

/**
 * Update a node
 */
export const useUpdateNode = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ nodeId, data: nodeData }) => {
            const { data } = await api.put(`/canvases/${canvasId}/nodes/${nodeId}`, nodeData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.nodes(canvasId) });
        }
    });
};

/**
 * Delete a node
 */
export const useDeleteNode = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (nodeId) => {
            await api.delete(`/canvases/${canvasId}/nodes/${nodeId}`);
            return nodeId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.nodes(canvasId) });
        }
    });
};

// ============================================
// Nested Edge Queries & Mutations
// ============================================

/**
 * Get edges for a canvas
 */
export const useCanvasEdges = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.edges(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/canvases/${canvasId}/edges`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Create an edge in a canvas
 */
export const useCreateEdge = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (edgeData) => {
            const { data } = await api.post(`/canvases/${canvasId}/edges`, edgeData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.edges(canvasId) });
        }
    });
};

/**
 * Delete an edge
 */
export const useDeleteEdge = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (edgeId) => {
            await api.delete(`/canvases/${canvasId}/edges/${edgeId}`);
            return edgeId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.edges(canvasId) });
        }
    });
};

// ============================================
// Node Group Queries & Mutations
// ============================================

/**
 * Get node groups for a canvas
 */
export const useCanvasNodeGroups = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.groups(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/canvas/${canvasId}/node-groups`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Get generated media for a canvas
 */
export const useCanvasGeneratedMedia = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.media(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/canvases/${canvasId}/generated-media`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Delete a generated media item
 */
export const useDeleteGeneratedMedia = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (mediaId) => {
            await api.delete(`/media-generations/${mediaId}`);
            return mediaId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.media(canvasId) });
        }
    });
};

/**
 * Create a node group
 */
export const useCreateNodeGroup = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (groupData) => {
            const { data } = await api.post('/node-groups', groupData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.groups(canvasId) });
        }
    });
};

/**
 * Update a node group
 */
export const useUpdateNodeGroup = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ groupId, data: groupData }) => {
            const { data } = await api.put(`/node-groups/${groupId}`, groupData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.groups(canvasId) });
        }
    });
};

/**
 * Delete a node group
 */
export const useDeleteNodeGroup = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (groupId) => {
            await api.delete(`/node-groups/${groupId}`);
            return groupId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.groups(canvasId) });
        }
    });
};

// ============================================
// Artboard Queries & Mutations
// ============================================

/**
 * Get artboards for a canvas
 */
export const useArtboards = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.artboards(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/artboards/canvas/${canvasId}`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Create an artboard
 */
export const useCreateArtboard = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (artboardData) => {
            const { data } = await api.post('/artboards', { ...artboardData, canvas_id: canvasId });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.artboards(canvasId) });
        }
    });
};

/**
 * Create default artboards (mobile, tablet, desktop)
 */
export const useCreateDefaultArtboards = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/artboards/canvas/${canvasId}/defaults`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.artboards(canvasId) });
        }
    });
};

/**
 * Update an artboard
 */
export const useUpdateArtboard = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ artboardId, data: artboardData }) => {
            const { data } = await api.put(`/artboards/${artboardId}`, artboardData);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.artboards(canvasId) });
        }
    });
};

/**
 * Delete an artboard
 */
export const useDeleteArtboard = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (artboardId) => {
            await api.delete(`/artboards/${artboardId}`);
            return artboardId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.artboards(canvasId) });
        }
    });
};

/**
 * Set active artboard
 */
export const useSetActiveArtboard = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (artboardId) => {
            const { data } = await api.put(`/artboards/canvas/${canvasId}/active/${artboardId}`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.artboards(canvasId) });
        }
    });
};

// ============================================
// UI Component Queries & Mutations
// ============================================

/**
 * Get UI components for a canvas (all artboards)
 */
export const useUIComponents = (canvasId) => {
    return useQuery({
        queryKey: canvasKeys.uiComponents(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/ui-components/canvas/${canvasId}`);
            return data;
        },
        enabled: !!canvasId
    });
};

/**
 * Get UI components for a specific artboard
 */
export const useUIComponentsByArtboard = (artboardId) => {
    return useQuery({
        queryKey: canvasKeys.uiComponentsByArtboard(artboardId),
        queryFn: async () => {
            const { data } = await api.get(`/ui-components/artboard/${artboardId}`);
            return data;
        },
        enabled: !!artboardId
    });
};

/**
 * Create a UI component
 */
export const useCreateUIComponent = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (componentData) => {
            const { data } = await api.post('/ui-components', { ...componentData, canvas_id: canvasId });
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponentsByArtboard(data.artboard_id) });
        }
    });
};

/**
 * Update a UI component
 */
export const useUpdateUIComponent = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ componentId, data: componentData }) => {
            const { data } = await api.put(`/ui-components/${componentId}`, componentData);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponentsByArtboard(data.artboard_id) });
        }
    });
};

/**
 * Delete a UI component
 */
export const useDeleteUIComponent = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (componentId) => {
            await api.delete(`/ui-components/${componentId}`);
            return componentId;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
        }
    });
};

/**
 * Sync component across artboards
 */
export const useSyncComponentAcrossArtboards = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ componentId, targetArtboardIds }) => {
            const { data } = await api.post(`/ui-components/${componentId}/sync`, {
                targetArtboardIds,
                canvasId
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
        }
    });
};

/**
 * Bring component forward
 */
export const useBringComponentForward = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (componentId) => {
            const { data } = await api.put(`/ui-components/${componentId}/bring-forward`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponentsByArtboard(data.artboard_id) });
        }
    });
};

/**
 * Send component backward
 */
export const useSendComponentBackward = (canvasId) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (componentId) => {
            const { data } = await api.put(`/ui-components/${componentId}/send-backward`);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponents(canvasId) });
            queryClient.invalidateQueries({ queryKey: canvasKeys.uiComponentsByArtboard(data.artboard_id) });
        }
    });
};

// ============================================
// Design Kit Queries
// ============================================

/**
 * Get the full design kit
 */
export const useDesignKit = () => {
    return useQuery({
        queryKey: designKitKeys.all,
        queryFn: async () => {
            const { data } = await api.get('/design-kit');
            return data;
        }
    });
};

/**
 * Get design kit components with optional filtering
 */
export const useDesignKitComponents = (layer = null, device = null) => {
    return useQuery({
        queryKey: designKitKeys.components(layer, device),
        queryFn: async () => {
            const params = new URLSearchParams();
            if (layer) params.append('layer', layer);
            if (device) params.append('device', device);
            const { data } = await api.get(`/design-kit/components?${params.toString()}`);
            return data;
        }
    });
};

/**
 * Get design kit tokens
 */
export const useDesignKitTokens = () => {
    return useQuery({
        queryKey: designKitKeys.tokens,
        queryFn: async () => {
            const { data } = await api.get('/design-kit/tokens');
            return data;
        }
    });
};

/**
 * Update a design kit component
 */
export const useUpdateDesignKitComponent = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ componentId, data: componentData }) => {
            const { data } = await api.put(`/design-kit/components/${componentId}`, componentData);
            return data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: designKitKeys.all });
            // Invalidate components queries as well
            if (data.layer) {
                queryClient.invalidateQueries({ queryKey: designKitKeys.components(data.layer) });
            }
        }
    });
};

/**
 * Get the merged design kit for a canvas (includes user-defined tokens/components)
 */
export const useMergedDesignKit = (canvasId) => {
    return useQuery({
        queryKey: designKitKeys.merged(canvasId),
        queryFn: async () => {
            const { data } = await api.get(`/design-kit/canvas/${canvasId}`);
            return data;
        },
        enabled: !!canvasId
    });
};
// ============================================
// Infinite Query Hooks (Paginated)
// ============================================

const DEFAULT_PAGE_SIZE = 24;

/**
 * Infinite query for media generations with cursor-based pagination
 * @param {string|null} canvasId - Optional canvas ID filter
 * @param {number} pageSize - Items per page (default 24)
 * @param {string|null} type - Optional type filter (e.g. 'video', 'image')
 */
export const useInfiniteMediaGenerations = (canvasId = null, pageSize = DEFAULT_PAGE_SIZE, type = null) => {
    const queryKey = ['media-generations', 'infinite'];
    if (canvasId) queryKey.push(canvasId);
    if (type) queryKey.push(type);

    return useInfiniteQuery({
        queryKey,
        queryFn: async ({ pageParam = null }) => {
            const params = new URLSearchParams({ limit: pageSize.toString() });
            if (pageParam) params.append('cursor', pageParam);
            if (canvasId) params.append('canvas_id', canvasId);
            if (type) params.append('type', type);
            const { data } = await api.get(`/media-generations?${params.toString()}`);
            return data;
        },
        getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
        initialPageParam: null,
        staleTime: 60000,
        refetchOnWindowFocus: false
    });
};

/**
 * Infinite query for storage files with pageToken-based pagination
 * @param {string} bucket - Storage bucket name
 * @param {number} pageSize - Items per page (default 24)
 */
export const useInfiniteStorageFiles = (bucket, pageSize = DEFAULT_PAGE_SIZE) => {
    return useInfiniteQuery({
        queryKey: ['storage', 'infinite', bucket],
        queryFn: async ({ pageParam = null }) => {
            const params = new URLSearchParams({ limit: pageSize.toString() });
            if (pageParam) params.append('pageToken', pageParam);
            const { data } = await api.get(`/storage/files/${bucket}?${params.toString()}`);
            return data;
        },
        getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
        enabled: !!bucket,
        initialPageParam: null,
        staleTime: 60000,
        refetchOnWindowFocus: false
    });
};

export default {
    useCanvases,
    useCanvas,
    useCreateCanvas,
    useUpdateCanvas,
    useDeleteCanvas,
    useCanvasNodes,
    useCreateNode,
    useUpdateNode,
    useDeleteNode,
    useCanvasEdges,
    useCreateEdge,
    useDeleteEdge,
    useCanvasNodeGroups,
    useCanvasGeneratedMedia,
    useDeleteGeneratedMedia,
    useCreateNodeGroup,
    useUpdateNodeGroup,
    useDeleteNodeGroup,
    // Artboards
    useArtboards,
    useCreateArtboard,
    useCreateDefaultArtboards,
    useUpdateArtboard,
    useDeleteArtboard,
    useSetActiveArtboard,
    // UI Components
    useUIComponents,
    useUIComponentsByArtboard,
    useCreateUIComponent,
    useUpdateUIComponent,
    useDeleteUIComponent,
    useSyncComponentAcrossArtboards,
    useBringComponentForward,
    useSendComponentBackward,
    // Design Kit
    useDesignKit,
    useMergedDesignKit,
    useDesignKitComponents,
    useDesignKitTokens,
    useUpdateDesignKitComponent,
    // Infinite Queries
    useInfiniteMediaGenerations,
    useInfiniteStorageFiles
};
